import { useState, useContext, useEffect, useReducer, useRef } from 'react';
import { executeAdminUserFormUpdate } from '../utils/submitAdminUserFormUpdate';
import { useStudentRoleSelection } from '../hooks/useStudentRoleSelection';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { batchesAPI, enrollmentsAPI, studentsAPI, usersAPI, mastersAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import AddEnrollmentModal from '../components/AddEnrollmentModal';
import LocationDropdowns from '../components/LocationDropdowns';
import SearchableSelect from '../components/Form/SearchableSelect';
import MessageDisplay from '../components/MessageDisplay';
import StaffPageShell from '../components/StaffPageShell';
import { AdminUserBasicInfoSection, AdminUserAdditionalContactFields, AdminUserFormFlowNav } from '../components/AdminUserForm';
import EditableEnrollFields from '../components/AdminAddUserForm/EditableEnrollFields';
import { ensureEnrollmentBatchFromForm } from '../hooks/useEnsureEnrollmentBatch';
import StudentChangeHistory from '../components/StudentList/StudentChangeHistory';
import {
  canCreateEnrollmentBatch,
  missingEnrollmentFieldsForSave,
} from '../domain/adminUser/enrollmentRequirement';
import { isPrivilegedStaffRole } from '../utils/permissions';
import { enrollmentFieldsFromBatch } from '../domain/adminUser/enrollSequence';
import { BATCH_SEARCHABLE_SELECT_PROPS } from '../domain/batch/batchDisplayLabel';
import {
  initialFormData,
  formReducer,
  useFormData,
  DateInput,
  EducationFields,
  PlacementFields,
  MarksFields
} from '../components/AdminAddUserForm';

const AdminEditUserPage = () => {
  const { user } = useContext(AuthContext);
  const { currentTheme } = useTheme();
  const token = user?.token || user?.user?.token;
  const userRole = user?.role || user?.user?.role;
  const navigate = useNavigate();
  const location = useLocation();

  const [editEmail, setEditEmail] = useState("");
  const [editData, setEditData] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsPage, setSearchResultsPage] = useState(1);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const SEARCH_RESULTS_PAGE_SIZE = 5;

  useEffect(() => {
    setSearchResultsPage(1);
  }, [searchResults]);

  // Extended reducer that includes LOAD_DATA case for edit functionality
  const extendedFormReducer = (state, action) => {
    if (action.type === 'LOAD_DATA') {
      // Ensure counsellingSessions is properly set - create a new array reference
      const counsellingSessionsToSet = Array.isArray(action.data?.counsellingSessions) 
        ? [...action.data.counsellingSessions] 
        : (action.data?.counsellingSessions || initialFormData.counsellingSessions);
      
      // Ensure placement is properly set - create a new object reference
      let placementToSet = initialFormData.placement;
      if (action.data?.placement) {
        if (typeof action.data.placement === 'object' && !Array.isArray(action.data.placement)) {
          placementToSet = { ...initialFormData.placement, ...action.data.placement };
        } else {
          placementToSet = action.data.placement;
        }
      }
      
      const newState = { 
        ...state, 
        ...action.data,
        // Explicitly set counsellingSessions to ensure it's not overridden
        counsellingSessions: counsellingSessionsToSet,
        // Explicitly set placement to ensure it's not overridden
        placement: placementToSet
      };
      return newState;
    }
    // Use the shared formReducer for all other actions
    return formReducer(state, action);
  };

  const [formData, dispatch] = useReducer(extendedFormReducer, initialFormData);
  
  // Use shared useFormData hook for data fetching
  const {
    courses,
    roles,
    educationOptions,
    universityOptions,
    companyOptions,
    heardFromOptions,
    courseCategories,
    domains,
    batches,
    counselors,
    refreshCompanyOptions,
    refreshBatches,
  } = useFormData(token);

  const batchSelectionRequest = useRef(0);
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [createBatchMessage, setCreateBatchMessage] = useState('');

  // Re-populate form data when dropdown options are loaded (fixes preselection issue)
  useEffect(() => {
    if (editData && formData && (educationOptions.length > 0 || universityOptions.length > 0 || courseCategories.length > 0 || domains.length > 0 || batches.length > 0)) {
      // Re-extract and set form data to ensure dropdowns are preselected
      const user = editData;
      const student = user.student || null;
      
      // Extract degree and university
      let degreeId = "";
      let universityId = "";
      
      if (student && student.academicQualifications && student.academicQualifications.length > 0) {
        student.academicQualifications.forEach((qual) => {
          if (qual.isHighestDegree) {
            degreeId = qual.qualificationType || "";
            universityId = qual.universityCollegeName || "";
          }
        });
      }
      
      // Extract enrollment data
      let batchId = "";
      let courseId = "";
      let courseCategoryId = "";
      let modeOfCourse = "";
      let locationOfCenter = "";
      let domainId = student?.domain?.id ? String(student.domain.id) : "";
      
      if (student && student.enrollments && student.enrollments.length > 0) {
        const latestEnrollment = student.enrollments[0];
        batchId = latestEnrollment.batch?.id ? String(latestEnrollment.batch.id) : "";
        courseId = latestEnrollment.batch?.course?.id ? String(latestEnrollment.batch.course.id) : "";
        courseCategoryId = latestEnrollment.batch?.course?.category?.id ? String(latestEnrollment.batch.course.category.id) : "";
        modeOfCourse = latestEnrollment.batch?.mode || "";
        locationOfCenter = latestEnrollment.batch?.location || "";
      }
      
      // Extract gender
      const gender = student?.gender || "";
      
      // Convert to strings for comparison
      const currentDegree = String(formData.degree || "");
      const currentUniversity = String(formData.university || "");
      const currentBatchId = String(formData.batchId || "");
      const currentCourseId = String(formData.courseId || "");
      const currentCourseCategoryId = String(formData.courseCategoryId || "");
      const currentDomainId = String(formData.domainId || "");
      
      const newDegree = String(degreeId);
      const newUniversity = String(universityId);
      const newBatchId = String(batchId);
      const newCourseId = String(courseId);
      const newCourseCategoryId = String(courseCategoryId);
      const newDomainId = String(domainId);
      
      // Extract gender and other fields
      const currentGender = String(formData.gender || "");
      const newGender = String(gender);
      const currentModeOfCourse = String(formData.modeOfCourse || "");
      const newModeOfCourse = String(modeOfCourse);
      const currentLocationOfCenter = String(formData.locationOfCenter || "");
      const newLocationOfCenter = String(locationOfCenter);
      
      // Fill a blank dropdown once options arrive. Do not write the loaded
      // enrollment back over a batch or course the operator just chose.
      if (!currentGender && newGender) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'gender',
          value: newGender
        });
      }
      if (!currentDegree && newDegree) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'degree',
          value: newDegree
        });
      }
      if (!currentUniversity && newUniversity) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'university',
          value: newUniversity
        });
      }
      if (!currentBatchId && newBatchId) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'batchId',
          value: newBatchId
        });
      }
      if (!currentCourseId && newCourseId) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'courseId',
          value: newCourseId
        });
      }
      if (!currentCourseCategoryId && newCourseCategoryId) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'courseCategoryId',
          value: newCourseCategoryId
        });
      }
      if (!currentDomainId && newDomainId) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'domainId',
          value: newDomainId
        });
      }
      if (!currentModeOfCourse && newModeOfCourse) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'modeOfCourse',
          value: newModeOfCourse
        });
      }
      if (!currentLocationOfCenter && newLocationOfCenter) {
        dispatch({
          type: 'UPDATE_FIELD',
          field: 'locationOfCenter',
          value: newLocationOfCenter
        });
      }
    }
  }, [editData, formData, educationOptions, universityOptions, courseCategories, domains, batches]);

  const { isStudentRole } = useStudentRoleSelection(roles, formData.role);
  const studentEnrollments =
    editData?.student?.enrollments || editData?.enrollments || [];
  const isLeadLikeStudent =
    isStudentRole && (!Array.isArray(studentEnrollments) || studentEnrollments.length === 0);

  // State for collapsible sections
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showAddEnrollmentModal, setShowAddEnrollmentModal] = useState(false);
  const [removingEnrollmentId, setRemovingEnrollmentId] = useState(null);

  // Helper function to get field style with color coding for unfilled fields
  const getFieldStyle = (fieldValue, isRequired = false) => {
    const isEmpty = !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;
    const baseStyle = {
      backgroundColor: 'var(--color-surface)',
      border: '2px solid var(--color-border)',
      color: 'var(--color-text)'
    };
    
    // Color code unfilled fields with a light yellow/orange background
    if (isEmpty && !isRequired) {
      return {
        ...baseStyle,
        backgroundColor: 'rgba(255, 193, 7, 0.1)', // Light yellow/orange tint
        border: '2px solid rgba(255, 193, 7, 0.3)'
      };
    }
    
    return baseStyle;
  };

  const fetchUserToEdit = async (emailOverride = null) => {
    const emailCandidate =
      typeof emailOverride === 'string'
        ? emailOverride
        : (typeof editEmail === 'string' ? editEmail : '');
    const searchQuery = emailCandidate.trim();
    if (!searchQuery) {
      setMessage("❌ Please enter student name or email");
      return;
    }

    setSearchLoading(true);
    setMessage("");
    
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let targetEmail = searchQuery.toLowerCase();
      setSearchResults([]);

      // Support searching by full name by resolving it to a student email.
      if (!emailRegex.test(searchQuery)) {
        const nameSearchResponse = await studentsAPI.filter({ fullName: searchQuery });
        const matchedStudents = Array.isArray(nameSearchResponse?.data)
          ? nameSearchResponse.data
          : [];

        if (matchedStudents.length === 0) {
          setMessage("❌ No student found with this name");
          setEditData(null);
          setSearchResults([]);
          return;
        }

        const exactMatch = matchedStudents.find((student) =>
          String(student.fullName || '').trim().toLowerCase() === searchQuery.toLowerCase()
        );

        if (!exactMatch && matchedStudents.length > 1) {
          setEditData(null);
          setSearchResults(matchedStudents);
          setMessage("ℹ️ Multiple students matched. Please select one to open the edit form.");
          return;
        }

        const chosenStudent = exactMatch || matchedStudents[0];
        const resolvedEmail = String(chosenStudent?.emailAddress || chosenStudent?.email || '').trim().toLowerCase();
        if (!resolvedEmail) {
          setMessage("❌ Matched student does not have a valid email");
          setEditData(null);
          setSearchResults([]);
          return;
        }

        targetEmail = resolvedEmail;
        setEditEmail(resolvedEmail);
      }

      const res = await usersAPI.getByEmail(targetEmail);
      
      if (res.data && (res.data._id || res.data.id)) {
        setEditData(res.data);
        setSearchResults([]);
        
        const user = res.data;
        const student = user.student || null;
        
        // Parse fullName to extract firstName, middleName, lastName (use API middleName when provided)
        let firstName = user.firstName || "";
        let middleName = user.middleName != null ? String(user.middleName) : "";
        let lastName = user.lastName || "";
        
        if (student && student.fullName) {
          const nameParts = student.fullName.trim().split(/\s+/);
          if (nameParts.length >= 3) {
            firstName = nameParts[0] || "";
            middleName = nameParts.slice(1, -1).join(" ") || "";
            lastName = nameParts[nameParts.length - 1] || "";
          } else if (nameParts.length === 2) {
            firstName = nameParts[0] || "";
            lastName = nameParts[1] || "";
            // keep middleName from API if we only have 2 parts
            if (middleName === "" && user.middleName != null) middleName = String(user.middleName);
          } else if (nameParts.length === 1) {
            firstName = nameParts[0] || "";
          }
        }
        
        // Extract academic qualifications
        let tenthMarks = "", tenthYear = "", tenthMarksType = "Percentage";
        let twelfthMarks = "", twelfthYear = "", twelfthMarksType = "Percentage";
        let btechMarks = "", btechYear = "", yearOfPassing = "", percentage = "";
        let btechMarksType = "Percentage", percentageType = "Percentage";
        let degreeId = "", universityId = "", btechInstitute = "", btechSpecialization = "";
        let educationGap = "";
        let stream = "", department = ""; // Specialization fields
        let hasGapInEducation = '', gapInEducationJustification = "";
        let hasGapAfterHighestEducation = '', gapAfterHighestEducationJustification = "";
        // M.Tech fields
        let mtechMarks = "", mtechYear = "", mtechMarksType = "Percentage";
        let mtechInstitute = "", mtechSpecialization = "";
        // PhD fields
        let phdDone = "No", phdYear = ""; // Default to "No" if no PhD qualification found
        
        if (student && student.academicQualifications && student.academicQualifications.length > 0) {
          student.academicQualifications.forEach((qual) => {
            // Handle Decimal type from Prisma (can be object with 'd' array or number)
            const parseDecimal = (val) => {
              if (!val) return "";
              if (typeof val === 'number') return String(val);
              if (typeof val === 'object' && val.d && Array.isArray(val.d)) {
                // Prisma Decimal format: {s: 1, e: 1, d: [73]}
                return String(val.d.join(''));
              }
              return String(val);
            };
            
            if (qual.qualificationType === '10th') {
              tenthMarks = parseDecimal(qual.percentageCgpa);
              tenthYear = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              // Use stored marksType or infer from value
              tenthMarksType = qual.marksType || (tenthMarks && parseFloat(tenthMarks) <= 10 ? 'CGPA' : 'Percentage');
            } else if (qual.qualificationType === '12th') {
              twelfthMarks = parseDecimal(qual.percentageCgpa);
              twelfthYear = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              // Use stored marksType or infer from value
              twelfthMarksType = qual.marksType || (twelfthMarks && parseFloat(twelfthMarks) <= 10 ? 'CGPA' : 'Percentage');
            } else if (qual.qualificationType === 'M.Tech') {
              // M.Tech qualification
              mtechMarks = parseDecimal(qual.percentageCgpa);
              mtechYear = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              mtechInstitute = qual.universityCollegeName || "";
              mtechSpecialization = qual.specializationBranch || "";
              mtechMarksType = qual.marksType || (mtechMarks && parseFloat(mtechMarks) <= 10 ? 'CGPA' : 'Percentage');
            } else if (qual.qualificationType === 'PhD') {
              // PhD qualification
              phdDone = "Yes";
              phdYear = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
            } else if (qual.qualificationType === 'B.Tech') {
              // Always map B.Tech — even when PhD/M.Tech is the highest degree
              degreeId = qual.qualificationType || "";
              universityId = qual.universityCollegeName || "";
              btechInstitute = qual.universityCollegeName || "";
              btechMarks = parseDecimal(qual.percentageCgpa);
              btechYear = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              yearOfPassing = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              percentage = parseDecimal(qual.percentageCgpa);
              const storedMarksType = qual.marksType || (percentage && parseFloat(percentage) <= 10 ? 'CGPA' : 'Percentage');
              btechMarksType = storedMarksType;
              percentageType = storedMarksType;
              educationGap = qual.gapJustification === 'Yes' ? "Yes" : (qual.gapJustification ? "Yes" : "No");
              stream = qual.specializationBranch || "";
              department = qual.specializationBranch || "";
              btechSpecialization = qual.specializationBranch || "";
              hasGapInEducation = qual.hasGapInEducation === true || qual.hasGapInEducation === 'Yes' || qual.hasGapInEducation === 'true' ? 'Yes' : (qual.hasGapInEducation === false || qual.hasGapInEducation === 'No' || qual.hasGapInEducation === 'false' ? 'No' : '');
              gapInEducationJustification = qual.gapInEducationJustification || "";
              hasGapAfterHighestEducation = qual.hasGapAfterHighestEducation === true || qual.hasGapAfterHighestEducation === 'Yes' || qual.hasGapAfterHighestEducation === 'true' ? 'Yes' : (qual.hasGapAfterHighestEducation === false || qual.hasGapAfterHighestEducation === 'No' || qual.hasGapAfterHighestEducation === 'false' ? 'No' : '');
              gapAfterHighestEducationJustification = qual.gapAfterHighestEducationJustification || "";
            } else if (qual.isHighestDegree) {
              // Legacy: Other highest degree types (only if B.Tech not already mapped)
              if (!btechMarks && !btechInstitute && !yearOfPassing) {
              degreeId = qual.qualificationType || "";
              universityId = qual.universityCollegeName || "";
              btechInstitute = qual.universityCollegeName || "";
              btechMarks = parseDecimal(qual.percentageCgpa);
              btechYear = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              yearOfPassing = qual.yearOfPassout ? String(qual.yearOfPassout) : "";
              percentage = parseDecimal(qual.percentageCgpa);
              const storedMarksType = qual.marksType || (percentage && parseFloat(percentage) <= 10 ? 'CGPA' : 'Percentage');
              btechMarksType = storedMarksType;
              percentageType = storedMarksType;
              educationGap = qual.gapJustification === 'Yes' ? "Yes" : (qual.gapJustification ? "Yes" : "No");
              stream = qual.specializationBranch || "";
              department = qual.specializationBranch || "";
              btechSpecialization = qual.specializationBranch || "";
              hasGapInEducation = qual.hasGapInEducation === true || qual.hasGapInEducation === 'Yes' || qual.hasGapInEducation === 'true' ? 'Yes' : (qual.hasGapInEducation === false || qual.hasGapInEducation === 'No' || qual.hasGapInEducation === 'false' ? 'No' : '');
              gapInEducationJustification = qual.gapInEducationJustification || "";
              hasGapAfterHighestEducation = qual.hasGapAfterHighestEducation === true || qual.hasGapAfterHighestEducation === 'Yes' || qual.hasGapAfterHighestEducation === 'true' ? 'Yes' : (qual.hasGapAfterHighestEducation === false || qual.hasGapAfterHighestEducation === 'No' || qual.hasGapAfterHighestEducation === 'false' ? 'No' : '');
              gapAfterHighestEducationJustification = qual.gapAfterHighestEducationJustification || "";
              }
            }
          });
        }
        
        // Extract enrollment data
        let batchId = "", courseId = "", batchStartDate = "", courseCompletionDate = "";
        let courseCategoryId = "";
        let domainId = student?.domain?.id ? String(student.domain.id) : "";
        let modeOfCourse = "", locationOfCenter = "";
        let batchStatus = "";
        let studentStatus = "";
        let attendancePercentage = "";
        let placementStatus = "";
        let remarks = "";
        let insuranceTaken = false, insuranceDeactivated = false;
        let certificationIssued = 'No', videoTestimonyReceived = 'No', testimonyReceived = 'No';
        let counsellingSessions = initialFormData.counsellingSessions;
        let placementData = {
          companyName: "",
          position: "",
          package: "",
          location: "",
          interviewDate: "",
          placementDate: "",
          joiningDate: "",
          result: "",
          offerDetails: ""
        };
        
        if (student && student.enrollments && student.enrollments.length > 0) {
          const latestEnrollment = student.enrollments[0]; // Most recent enrollment
          
          batchId = latestEnrollment.batch?.id ? String(latestEnrollment.batch.id) : "";
          courseId = latestEnrollment.batch?.course?.id ? String(latestEnrollment.batch.course.id) : "";
          courseCategoryId = latestEnrollment.batch?.course?.category?.id ? String(latestEnrollment.batch.course.category.id) : "";
          batchStartDate = latestEnrollment.enrollmentDate ? new Date(latestEnrollment.enrollmentDate).toISOString().split('T')[0] : "";
          courseCompletionDate = latestEnrollment.courseCompletionDate ? new Date(latestEnrollment.courseCompletionDate).toISOString().split('T')[0] : "";
          batchStatus = latestEnrollment.batchStatus || "";
          studentStatus = latestEnrollment.batchStatus || "";
          attendancePercentage = latestEnrollment.attendancePercentage ? String(latestEnrollment.attendancePercentage) : "";
          remarks = latestEnrollment.remarks || "";
          // Map old placementStatus values to new
          const oldPlacementStatus = latestEnrollment.placementStatus || "";
          const statusMap = {
            'Done': 'Placed',
            'InProcess': 'In progress',
            'Not Started': 'Not interested'
          };
          placementStatus = statusMap[oldPlacementStatus] || oldPlacementStatus;
          // Extract mode and location from batch
          modeOfCourse = latestEnrollment.batch?.mode || "";
          locationOfCenter = latestEnrollment.batch?.location || "";
          // Extract insurance fields
          insuranceTaken = latestEnrollment.insuranceTaken === true || latestEnrollment.insuranceTaken === 'Yes' || latestEnrollment.insuranceTaken === 'true' ? 'Yes' : (latestEnrollment.insuranceTaken === false || latestEnrollment.insuranceTaken === 'No' || latestEnrollment.insuranceTaken === 'false' ? 'No' : '');
          insuranceDeactivated = latestEnrollment.insuranceDeactivated === true || latestEnrollment.insuranceDeactivated === 'Yes' || latestEnrollment.insuranceDeactivated === 'true' ? 'Yes' : (latestEnrollment.insuranceDeactivated === false || latestEnrollment.insuranceDeactivated === 'No' || latestEnrollment.insuranceDeactivated === 'false' ? 'No' : '');
          // Extract certificate and testimony fields
          certificationIssued = latestEnrollment.certificationIssued === true || latestEnrollment.certificationIssued === 'Yes' || latestEnrollment.certificationIssued === 'true' ? 'Yes' : (latestEnrollment.certificationIssued === false || latestEnrollment.certificationIssued === 'No' || latestEnrollment.certificationIssued === 'false' ? 'No' : 'No');
          videoTestimonyReceived = latestEnrollment.videoTestimonyReceived === true || latestEnrollment.videoTestimonyReceived === 'Yes' || latestEnrollment.videoTestimonyReceived === 'true' ? 'Yes' : (latestEnrollment.videoTestimonyReceived === false || latestEnrollment.videoTestimonyReceived === 'No' || latestEnrollment.videoTestimonyReceived === 'false' ? 'No' : 'No');
          testimonyReceived = latestEnrollment.testimonyReceived === true || latestEnrollment.testimonyReceived === 'Yes' || latestEnrollment.testimonyReceived === 'true' ? 'Yes' : (latestEnrollment.testimonyReceived === false || latestEnrollment.testimonyReceived === 'No' || latestEnrollment.testimonyReceived === 'false' ? 'No' : 'No');
          
          // Extract counselling sessions
          if (latestEnrollment.counsellingSessions && latestEnrollment.counsellingSessions.length > 0) {
            counsellingSessions = latestEnrollment.counsellingSessions.slice(0, 4).map((session, idx) => {
              // Handle date conversion - try multiple formats
              let sessionDate = '';
              if (session.sessionDate) {
                try {
                  const dateObj = new Date(session.sessionDate);
                  if (!isNaN(dateObj.getTime())) {
                    sessionDate = dateObj.toISOString().split('T')[0];
                  }
                } catch (e) {
                  console.error(`❌ Error parsing date for session ${idx + 1}:`, e, session.sessionDate);
                }
              }
              
              // Handle counselorId - check multiple possible paths
              let counselorId = '';
              if (session.counselor?.id) {
                counselorId = String(session.counselor.id);
              } else if (session.counselorId) {
                counselorId = String(session.counselorId);
              } else if (session.counselor) {
                // If counselor is an object but doesn't have id, try to get it
                counselorId = String(session.counselor.id || session.counselor.counselorId || '');
              }
              
              const mappedSession = {
                sessionDate: sessionDate,
                counselorId: counselorId,
                remarks: session.remarks || ''
              };
              return mappedSession;
            });
            // Pad to 4 sessions if needed
            while (counsellingSessions.length < 4) {
              counsellingSessions.push({ sessionDate: '', counselorId: '', remarks: '' });
            }
          } else {
            // Ensure we have 4 empty sessions
            counsellingSessions = [
              { sessionDate: '', counselorId: '', remarks: '' },
              { sessionDate: '', counselorId: '', remarks: '' },
              { sessionDate: '', counselorId: '', remarks: '' },
              { sessionDate: '', counselorId: '', remarks: '' }
            ];
          }
          
          // Extract placement data
          if (latestEnrollment.placement) {
            const placement = latestEnrollment.placement;
            
            // Handle Decimal type from Prisma for package
            const parseDecimal = (val) => {
              if (!val) return "";
              if (typeof val === 'number') return String(val);
              if (typeof val === 'object' && val.d && Array.isArray(val.d)) {
                // Prisma Decimal format: {s: 1, e: 1, d: [73]}
                return String(val.d.join(''));
              }
              return String(val);
            };
            
            // Extract placementDate
            const placementDateValue = placement.placementDate ? new Date(placement.placementDate).toISOString().split('T')[0] : "";
            
            // Parse structured feedbackRemarks to extract separate fields
            // Format: "Interview Result: <result> | Offer Details: <details> | Location: <location> | <other notes>"
            let location = "";
            let interviewResult = "";
            let offerDetails = "";
            const feedbackRemarks = placement.feedbackRemarks || placement.offerDetails || placement.remarks || "";
            
            if (feedbackRemarks) {
              // Extract Interview Result - use case-insensitive match
              const resultMatch = feedbackRemarks.match(/Interview Result:\s*([^|]+)/i);
              if (resultMatch && resultMatch[1]) {
                interviewResult = String(resultMatch[1]).trim();
              }
              
              // Extract Offer Details - use case-insensitive match
              const offerMatch = feedbackRemarks.match(/Offer Details:\s*([^|]+)/i);
              if (offerMatch && offerMatch[1]) {
                offerDetails = String(offerMatch[1]).trim();
              }
              
              // Extract Location - use case-insensitive match
              const locationMatch = feedbackRemarks.match(/Location:\s*([^|]+)/i);
              if (locationMatch && locationMatch[1]) {
                location = String(locationMatch[1]).trim();
              }
              
              // If no structured format found, try to parse old format or use as-is
              if (!interviewResult && !offerDetails && !location) {
                // Old format: might have location or other unstructured data
                if (feedbackRemarks.toLowerCase().includes("location:")) {
                  const oldLocationMatch = feedbackRemarks.match(/Location:\s*([^|]+)/i);
                  if (oldLocationMatch && oldLocationMatch[1]) {
                    location = String(oldLocationMatch[1]).trim();
                    let otherNotes = String(feedbackRemarks || '');
                    otherNotes = otherNotes.replace(/Location:\s*[^|]+\s*\|\s*/i, "").replace(/Location:\s*[^|]+/i, "").trim();
                    if (otherNotes && otherNotes !== 'null' && otherNotes !== 'undefined') {
                      offerDetails = otherNotes;
                    }
                  } else {
                    offerDetails = feedbackRemarks || '';
                  }
                } else {
                  offerDetails = feedbackRemarks || '';
                }
              }
            }
            
            // Also check direct fields from placement object (in case they're stored separately)
            if (!location && (placement.location || placement.workLocation)) {
              location = placement.location || placement.workLocation;
            }
            if (!interviewResult && (placement.result || placement.status)) {
              interviewResult = placement.result || placement.status;
            }
            if (!offerDetails && placement.offerDetails) {
              offerDetails = placement.offerDetails;
            }
            
            placementData = {
              // companyName should be the companyId for the dropdown, not the name
              companyName: placement.companyId ? String(placement.companyId) : (placement.company?.id ? String(placement.company.id) : ""),
              position: placement.jobRole || placement.position || "",
              package: placement.startingPackage ? parseDecimal(placement.startingPackage) : (placement.currentPackage ? parseDecimal(placement.currentPackage) : (placement.package ? String(placement.package) : "")),
              location: location || placement.location || placement.workLocation || "",
              interviewDate: placement.interviewDate ? new Date(placement.interviewDate).toISOString().split('T')[0] : (placement.offerDate ? new Date(placement.offerDate).toISOString().split('T')[0] : ""),
              placementDate: placementDateValue,
              joiningDate: placement.joiningDate ? new Date(placement.joiningDate).toISOString().split('T')[0] : "",
              result: interviewResult || placement.status || placement.result || "",
              offerDetails: offerDetails,
              shouldMoveToAlumni: placement.shouldMoveToAlumni === false || placement.shouldMoveToAlumni === 'false' || placement.shouldMoveToAlumni === 'No' ? 'No' : 'Yes',
              placementGroupRemovalReason: placement.placementGroupRemovalReason || "",
              removedFromPlacementGroup: placement.removedFromPlacementGroup || false
            };
          }
        }
        
        // Populate form data with user and student data using dispatch
        const formDataToLoad = {
            firstName: firstName,
            middleName: middleName,
            lastName: lastName,
            email: user.email || "",
            mobile: student?.phoneNumber || "",
            whatsapp: student?.whatsapp || student?.phoneNumber || "",
            linkedIn: (student?.linkedIn !== null && student?.linkedIn !== undefined) ? student.linkedIn : "",
            department: department,
            gender: student?.gender || "",
            // currentCity: from currentLocation, or currentCity, or derive from currentAddress
            currentCity: student?.currentLocation || student?.currentCity || (student?.currentAddress ? 
              (student.currentAddress.includes(',') ? 
                student.currentAddress.split(',').pop().trim() : 
                student.currentAddress.trim()) : ""),
            diplomaDone: student?.diplomaDone || "",
            stream: stream,
            semester: "",
            tenthMarks: tenthMarks,
            tenthMarksType: tenthMarksType,
            twelfthMarks: twelfthMarks,
            twelfthMarksType: twelfthMarksType,
            btechMarks: btechMarks,
            btechMarksType: btechMarksType,
            tenthYear: tenthYear,
            twelfthYear: twelfthYear,
            btechYear: btechYear,
            mtechMarks: mtechMarks,
            mtechYear: mtechYear,
            mtechMarksType: mtechMarksType,
            mtechInstitute: mtechInstitute,
            mtechSpecialization: mtechSpecialization,
            phdDone: phdDone,
            phdYear: phdYear,
            educationGap: educationGap,
            hasGapInEducation: hasGapInEducation,
            gapInEducationJustification: gapInEducationJustification,
            hasGapAfterHighestEducation: hasGapAfterHighestEducation,
            gapAfterHighestEducationJustification: gapAfterHighestEducationJustification,
            profilePreference: student?.profilePreference || "",
            modeOfCourse: modeOfCourse,
            locationOfCenter: locationOfCenter,
            yearOfPassing: yearOfPassing,
            percentage: percentage,
            percentageType: percentageType,
            btechInstitute: btechInstitute,
            btechSpecialization: btechSpecialization,
            motivation: student?.motivation || "",
            resumeUrl: student?.resumeUrl || "",
            bio: student?.bio || "",
            notes: student?.notes || "",
            pincode: student?.pincode || "",
            address: student?.currentAddress || "",
            permanentAddress: student?.permanentAddress || "",
            joiningKitAddress: student?.joiningKitAddress || "",
            joiningKitReceived: student?.joiningKitReceived || "",
            parentsName: student?.parentsName || "",
            parentsPhone: student?.parentsPhone || "",
            parentsEmail: student?.parentsEmail || "",
            guardianName: student?.guardianName || "",
            guardianContact: student?.guardianContact || "",
            guardianEmail: student?.guardianEmail || "",
            employeeId: student?.employeeId || "",
            experienceYears: student?.experienceYears ? String(student.experienceYears) : "",
            availability: student?.availability || "",
            dob: student?.dob ? new Date(student.dob).toISOString().split('T')[0] : "",
            degree: String(degreeId),
            university: String(universityId),
            country: student?.countryId || "",
            state: student?.stateId || "",
            cityId: student?.cityId || "",
            heardFrom: student?.heardFromId || "",
            role: user.role || "",
            courseId: String(courseId),
            batchId: String(batchId),
            courseCategoryId: String(courseCategoryId),
            domainId: String(domainId),
            batchStartDate: batchStartDate,
            courseCompletionDate: courseCompletionDate,
            attendancePercentage: attendancePercentage,
            studentStatus: studentStatus,
            batchStatus: batchStatus,
            placementStatus: placementStatus,
            remarks: remarks,
            insuranceTaken: insuranceTaken,
            insuranceDeactivated: insuranceDeactivated,
            certificationIssued: certificationIssued,
            videoTestimonyReceived: videoTestimonyReceived,
            testimonyReceived: testimonyReceived,
            veStudentId: student?.id || "",
            password: "",
            counsellingSessions: counsellingSessions,
            placement: placementData
          };
        dispatch({
          type: 'LOAD_DATA',
          data: formDataToLoad
        });

        setMessage("✅ User found! You can now edit their details below.");
      } else {
        setMessage("❌ User not found");
        setEditData(null);
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setMessage("❌ Error fetching user. Please check the entered name/email.");
      setEditData(null);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchUserByStudentId = async (studentId) => {
    if (!studentId) return;
    try {
      const res = await studentsAPI.getById(studentId);
      const student = res?.data;
      const emailFromStudent = String(student?.emailAddress || '').trim().toLowerCase();
      if (emailFromStudent) {
        setEditEmail(emailFromStudent);
        await fetchUserToEdit(emailFromStudent);
      } else {
        setMessage('❌ Student email not found for this record.');
      }
    } catch (error) {
      console.error('Error fetching student by ID for view fallback:', error);
      setMessage('❌ Unable to load student details from View link.');
    }
  };

  // Support deep-link from student management "View" button:
  // /admin/edit-user?email=<student-email>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromQuery = params.get('email');
    const studentIdFromQuery = params.get('studentId');
    if (!token) return;

    if (emailFromQuery) {
      const normalizedEmail = emailFromQuery.trim().toLowerCase();
      setEditEmail(normalizedEmail);
      fetchUserToEdit(normalizedEmail);
      return;
    }

    if (studentIdFromQuery) {
      fetchUserByStudentId(studentIdFromQuery.trim());
    }
  }, [location.search, token]);

  const handleBatchSelection = async (batchId) => {
    if (!batchId) return;
    const requestId = ++batchSelectionRequest.current;
    try {
      const response = await batchesAPI.getById(batchId);
      if (requestId !== batchSelectionRequest.current) return;
      const batch = response.data?.data || response.data;
      if (batch) {
        const fields = enrollmentFieldsFromBatch(batch);
        Object.entries(fields).forEach(([field, value]) => {
          dispatch({ type: 'UPDATE_FIELD', field, value });
        });
        if (batch.mode) {
          dispatch({ type: 'UPDATE_FIELD', field: 'modeOfCourse', value: batch.mode });
        }
        if (batch.mode === 'Offline' && batch.location) {
          dispatch({ type: 'UPDATE_FIELD', field: 'locationOfCenter', value: batch.location });
        }
        if (batch.courseId) {
          dispatch({ type: 'UPDATE_FIELD', field: 'courseId', value: String(batch.courseId) });
          if (batch.course?.categoryId) {
            dispatch({ type: 'UPDATE_FIELD', field: 'courseCategoryId', value: String(batch.course.categoryId) });
          } else if (batch.course?.category?.id) {
            dispatch({ type: 'UPDATE_FIELD', field: 'courseCategoryId', value: String(batch.course.category.id) });
          }
        }
        if (batch.startDate) {
          dispatch({ type: 'UPDATE_FIELD', field: 'batchStartDate', value: batch.startDate.split('T')[0] });
        }
        if (batch.expectedCompletionDate) {
          dispatch({ type: 'UPDATE_FIELD', field: 'courseCompletionDate', value: batch.expectedCompletionDate.split('T')[0] });
        } else if (batch.tentativeEndDate) {
          dispatch({ type: 'UPDATE_FIELD', field: 'courseCompletionDate', value: batch.tentativeEndDate.split('T')[0] });
        }
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
    }
  };

  const removeEnrollment = async (enrollment) => {
    const batchName = enrollment?.batch?.batchName || 'this batch';
    const confirmed = window.confirm(
      `Remove the enrollment in ${batchName}? Fees, sessions, and placement stored on this enrollment are removed. The student keeps the other enrollment.`
    );
    if (!confirmed) return;
    setRemovingEnrollmentId(enrollment.id);
    setMessage('');
    try {
      await enrollmentsAPI.delete(enrollment.id);
      await fetchUserToEdit();
      setMessage('Enrollment removed.');
    } catch (err) {
      setMessage(getApiErrorMessage(err, 'Could not remove the enrollment'));
    } finally {
      setRemovingEnrollmentId(null);
    }
  };

  const handleCreateBatchFromForm = async () => {
    if (!canCreateEnrollmentBatch(formData)) {
      setCreateBatchMessage('Fill course, mode, and batch start date first.');
      return;
    }
    setCreatingBatch(true);
    setCreateBatchMessage('');
    try {
      const resolved = await ensureEnrollmentBatchFromForm(formData);
      const batchId = resolved?.id != null ? String(resolved.id) : '';
      if (batchId) {
        dispatch({ type: 'UPDATE_FIELD', field: 'batchId', value: batchId });
        await handleBatchSelection(batchId);
      }
      if (typeof refreshBatches === 'function') await refreshBatches();
      const name = resolved?.batchName || resolved?.name || batchId;
      setCreateBatchMessage(
        resolved?.created
          ? `Created batch “${name}” and selected it.`
          : `Using existing batch “${name}”.`
      );
    } catch (error) {
      setCreateBatchMessage(
        error.response?.data?.message || error.message || 'Could not create batch'
      );
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleChange = (e) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    
    dispatch({ type: 'UPDATE_FIELD', field: fieldName, value: fieldValue });
    
    // When batch is selected, populate mode and other batch-related fields
    if (fieldName === 'batchId' && fieldValue) {
      handleBatchSelection(fieldValue);
    }
    
    // Diploma No only clears the diploma semester. B.Tech specialization stays.
    if (fieldName === 'diplomaDone' && fieldValue === 'No') {
      dispatch({ type: 'UPDATE_FIELD', field: 'semester', value: '' });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editData) {
      setMessage("❌ Please search for a user first");
      return;
    }

    // Frontend validation - require all key fields to be filled when editing
    const requiredFields = [
      { key: 'firstName', label: 'First Name' },
      { key: 'lastName', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'mobile', label: 'Mobile' }
    ];
    if (isStudentRole && !isLeadLikeStudent) {
      requiredFields.push({ key: 'veStudentId', label: 'VE Student ID' });
    }
    const missingRequired = requiredFields.filter(({ key }) => {
      const val = formData[key];
      return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
    });
    if (missingRequired.length > 0) {
      const missingLabels = missingRequired.map(({ label }) => label).join(', ');
      setMessage(`❌ Please fill all required fields. Missing: ${missingLabels}`);
      return;
    }

    if (isStudentRole) {
      const missingEnroll = missingEnrollmentFieldsForSave(formData, {
        isLeadLike: isLeadLikeStudent,
      });
      if (missingEnroll.length) {
        setMessage(`❌ Please complete enrollment fields: ${missingEnroll.join(', ')}`);
        return;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage("❌ Please enter a valid email address");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const payload = { ...formData };
      if (
        isStudentRole &&
        !String(payload.batchId || '').trim() &&
        canCreateEnrollmentBatch(payload)
      ) {
        const resolved = await ensureEnrollmentBatchFromForm(payload);
        if (resolved?.id) payload.batchId = String(resolved.id);
      }
      await executeAdminUserFormUpdate({
        userId: editData._id || editData.id,
        formData: payload,
        companyOptions,
        refreshCompanyOptions,
      });

      setMessage('✅ User updated successfully!');

      setTimeout(() => {
        fetchUserToEdit();
      }, 500);
    } catch (error) {
      console.error("Error updating user:", error);
      
      // Handle validation errors with detailed messages
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(err => 
          `• ${err.field}: ${err.message}`
        ).join('\n');
        setMessage(`❌ Validation failed:\n${errorMessages}`);
      } else if (error.response?.data?.message) {
        setMessage(`❌ ${error.response.data.message}`);
      } else {
        setMessage("❌ Failed to update user. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditEmail("");
    setEditData(null);
    setSearchResults([]);
    setSearchResultsPage(1);
    dispatch({ type: 'RESET' });
    setMessage("");
  };

  if (!isPrivilegedStaffRole(userRole)) {
    navigate('/dashboard');
    return null;
  }

  return (
    <StaffPageShell title="Edit User" subtitle="Search and edit user details">
        {/* Search Section */}
      <div className="profile-card theme-animate p-8 w-full max-w-4xl mb-6">
        <h2 className="text-2xl font-bold mb-6 theme-text-gradient text-center">Find User</h2>
          <div className="flex gap-3">
          <input
              type="text"
              placeholder="Enter student name or email to edit"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              disabled={searchLoading}
            className="flex-1 px-4 py-3 theme-input rounded-lg theme-focus"
            style={{ 
              backgroundColor: 'var(--color-surface)',
              border: '2px solid var(--color-border)',
              color: 'var(--color-text)'
            }}
          />
          <button
              type="button"
              onClick={() => fetchUserToEdit()}
              disabled={searchLoading}
            className="px-6 py-3 rounded-lg font-medium transition-all duration-200 theme-hover-lift theme-focus"
              style={{ 
                backgroundColor: searchLoading ? 'var(--color-neutral-400)' : 'var(--color-primary)',
              color: 'white',
                cursor: searchLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {searchLoading ? "Searching..." : "Search User"}
          </button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-4 rounded-lg p-4" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
              <p className="text-sm font-medium mb-3" style={{ color: 'var(--color-text)' }}>
                Multiple matches found. Select a student:
              </p>
              <div className="space-y-2">
                {searchResults
                  .slice((searchResultsPage - 1) * SEARCH_RESULTS_PAGE_SIZE, searchResultsPage * SEARCH_RESULTS_PAGE_SIZE)
                  .map((student) => {
                  const studentEmail = String(student?.emailAddress || student?.email || '').trim();
                  return (
                    <button
                      key={student.id}
                      type="button"
                      onClick={() => fetchUserToEdit(studentEmail)}
                      disabled={!studentEmail || searchLoading}
                      className="w-full text-left px-3 py-2 rounded-lg theme-hover-lift"
                      style={{
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text)',
                        cursor: !studentEmail || searchLoading ? 'not-allowed' : 'pointer',
                        opacity: !studentEmail || searchLoading ? 0.6 : 1
                      }}
                    >
                      <div className="font-medium">
                        {student.fullName || 'N/A'}{' '}
                        <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                          ({studentEmail || 'No email available'})
                        </span>
                      </div>
                      <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        Student ID: {student.id}
                        {student.unixUsername ? ` · Unix: ${student.unixUsername}` : ''}
                      </div>
                    </button>
                  );
                })}
              </div>
              {searchResults.length > SEARCH_RESULTS_PAGE_SIZE && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    Page {searchResultsPage} of {Math.ceil(searchResults.length / SEARCH_RESULTS_PAGE_SIZE)}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSearchResultsPage((prev) => Math.max(1, prev - 1))}
                      disabled={searchResultsPage === 1}
                      className="px-3 py-1 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text)',
                        opacity: searchResultsPage === 1 ? 0.5 : 1,
                        cursor: searchResultsPage === 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setSearchResultsPage((prev) =>
                          Math.min(Math.ceil(searchResults.length / SEARCH_RESULTS_PAGE_SIZE), prev + 1)
                        )
                      }
                      disabled={searchResultsPage >= Math.ceil(searchResults.length / SEARCH_RESULTS_PAGE_SIZE)}
                      className="px-3 py-1 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'var(--color-background)',
                        border: '1px solid var(--color-border)',
                        color: 'var(--color-text)',
                        opacity: searchResultsPage >= Math.ceil(searchResults.length / SEARCH_RESULTS_PAGE_SIZE) ? 0.5 : 1,
                        cursor: searchResultsPage >= Math.ceil(searchResults.length / SEARCH_RESULTS_PAGE_SIZE) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Edit Form - Only show when user is found */}
        {editData && (
        <form
          onSubmit={handleEditSubmit}
          className="profile-card theme-animate p-8 w-full max-w-4xl"
        >
          <h2 className="text-2xl font-bold mb-6 theme-text-gradient text-center">Edit User: {editData.firstName} {editData.lastName}</h2>

          <MessageDisplay message={message} />

          <AdminUserFormFlowNav
            isStudent={isStudentRole}
            mode="edit"
            enabled={Boolean(editData)}
            additionalExpanded={showAdditionalFields}
            onExpandAdditional={setShowAdditionalFields}
          />

          <AdminUserBasicInfoSection
            layoutVariant="edit"
            formData={formData}
            onChange={handleChange}
            roles={roles}
            isStudentRole={isStudentRole}
            getFieldStyle={getFieldStyle}
          />

          {/* Batch & Course Information - Student Role */}
          {isStudentRole && (
            <>
              <div id="auf-batch-course" className="auf-form-section scroll-mt-28 mb-6 border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Batch & Course Information</h3>
                <p className="text-sm text-gray-500 mb-4">
                  {isLeadLikeStudent
                    ? 'This student has no enrollment yet (lead). Batch is optional — fill fields and use Create batch when ready.'
                    : 'Change the batch or edit program, course, category, mode, center, and dates. Use Create batch to add a new batch now, or one is created on save if needed.'}
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                    Batch Name{isLeadLikeStudent ? ' (optional)' : ''}
                  </label>
                  <SearchableSelect
                    name="batchId"
                    value={formData.batchId}
                    onChange={handleChange}
                    className="w-full"
                    placeholder={isLeadLikeStudent ? 'Select Batch (optional)' : 'Select Batch'}
                    options={batches}
                    {...BATCH_SEARCHABLE_SELECT_PROPS}
                  />
                </div>

                {editData?.student?.id && (
                  <div
                    className="mb-4 p-4 rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      border: '1px solid var(--color-border)'
                    }}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <h4 className="font-semibold" style={{ color: 'var(--color-text)' }}>
                        Course enrollments
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowAddEnrollmentModal(true)}
                        className="px-3 py-1.5 rounded-md text-sm font-medium text-white"
                        style={{ backgroundColor: 'var(--color-primary)' }}
                      >
                        Add enrollment
                      </button>
                    </div>
                    <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Additional batches add a new enrollment row. When a student has more than
                      one, Remove drops that enrollment and keeps the rest.
                    </p>
                    {(editData.student.enrollments || []).length === 0 ? (
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        No enrollment records returned for this student.
                      </p>
                    ) : (
                      <ul className="text-sm space-y-2" style={{ color: 'var(--color-text)' }}>
                        {(editData.student.enrollments || []).map((en) => (
                          <li key={en.id} className="flex flex-wrap items-center justify-between gap-2">
                            <span>
                              {en.batch?.batchName || 'Batch'} — {en.batch?.course?.courseName || 'Course'}{' '}
                              <span className="text-gray-500">({en.batchStatus || '—'})</span>
                            </span>
                            {(editData.student.enrollments || []).length > 1 ? (
                              <button
                                type="button"
                                className="text-sm font-medium text-red-700 hover:text-red-900 disabled:opacity-50"
                                disabled={String(removingEnrollmentId) === String(en.id)}
                                onClick={() => removeEnrollment(en)}
                              >
                                {String(removingEnrollmentId) === String(en.id) ? 'Removing…' : 'Remove'}
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <EditableEnrollFields
                  formData={formData}
                  dispatch={dispatch}
                  courses={courses}
                  courseCategories={courseCategories}
                  batches={batches}
                  getFieldStyle={getFieldStyle}
                  batchOptional={isLeadLikeStudent}
                  onCreateBatch={handleCreateBatchFromForm}
                  creatingBatch={creatingBatch}
                  createBatchMessage={createBatchMessage}
                />

                {/* Domain - editable at student level */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                    Domain
                  </label>
                  <SearchableSelect
                    name="domainId"
                    value={formData.domainId || ''}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="Select Domain"
                    options={domains}
                    getOptionValue={(domain) => (typeof domain === 'object' ? domain.id : domain)}
                    getOptionLabel={(domain) => (typeof domain === 'object' ? (domain.domainName || domain.name) : domain)}
                  />
                </div>

              </div>
            </>
          )}

          {/* Additional Fields Section - Collapsible */}
          <div className="border-t pt-6 mt-6">
            <button
              type="button"
              onClick={() => setShowAdditionalFields(!showAdditionalFields)}
              className="w-full mb-4 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-200 flex items-center justify-between"
            >
              <span>{showAdditionalFields ? '▼' : '▶'} Additional Fields</span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {showAdditionalFields ? 'Click to collapse' : 'Click to expand'}
              </span>
            </button>

            {showAdditionalFields && (
              <>
                {/* Student Role Additional Fields */}
                {isStudentRole && (
                  <>
                    <AdminUserAdditionalContactFields
                      formData={formData}
                      onChange={handleChange}
                      getFieldStyle={getFieldStyle}
                    />
                    <div id="auf-enrollment-status" className="auf-form-section scroll-mt-28">
                    {/* Attendance Percentage */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        Attendance (%)
                      </label>
                      <input
                        type="number"
                        name="attendancePercentage"
                        placeholder="Enter attendance percentage (0-100)"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.attendancePercentage || ''}
                        onChange={handleChange}
                        className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.attendancePercentage)}
                      />
                    </div>

                    {/* Student Status */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        Student Status
                      </label>
                      <SearchableSelect
                        name="studentStatus"
                        value={formData.studentStatus || formData.batchStatus || 'Ongoing'}
                        onChange={handleChange}
                        required
                        className="w-full"
                        options={[
                          { value: 'Ongoing', label: 'Course Ongoing' },
                          { value: 'Completed', label: 'Course Completed' },
                          { value: 'On Hold', label: 'On Hold' },
                          { value: 'Left in between', label: 'Left in between' },
                        ]}
                      />
                    </div>

                    {/* Remarks (if on hold) */}
                    {(formData.studentStatus === 'On Hold' || formData.batchStatus === 'On Hold') && (
                      <div className="mb-4 space-y-3">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                          Remarks (Reason for hold)
                        </label>
                        <textarea
                          name="remarks"
                          placeholder="Enter reason for hold"
                          value={formData.remarks || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.remarks)}
                          rows="3"
                        />
                        <div>
                          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                            Hold till
                          </label>
                          <input
                            type="date"
                            name="holdTillDate"
                            value={formData.holdTillDate || ''}
                            onChange={handleChange}
                            className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                            style={getFieldStyle(formData.holdTillDate)}
                          />
                        </div>
                      </div>
                    )}

                    {/* Placement Status */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                        Placement Status
                      </label>
                      <SearchableSelect
                        name="placementStatus"
                        value={formData.placementStatus || ''}
                        onChange={handleChange}
                        className="w-full"
                        placeholder="Select Placement Status"
                        options={[
                          { value: 'Placed', label: 'Placed' },
                          { value: 'In progress', label: 'In progress' },
                          { value: 'Hold', label: 'Hold' },
                          { value: 'Under Preparation', label: 'Under Preparation' },
                          { value: 'Not interested', label: 'Not interested' },
                        ]}
                      />
                    </div>
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                          Applied for Truechip
                        </label>
                        <SearchableSelect
                          name="appliedForTruechip"
                          value={formData.appliedForTruechip || ''}
                          onChange={handleChange}
                          className="w-full"
                          placeholder="—"
                          options={[
                            { value: 'Yes', label: 'Yes' },
                            { value: 'No', label: 'No' },
                          ]}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                          Truechip result
                        </label>
                        <SearchableSelect
                          name="truechipResult"
                          value={formData.truechipResult || ''}
                          onChange={handleChange}
                          className="w-full"
                          placeholder="—"
                          options={[
                            { value: 'Passed', label: 'Passed' },
                            { value: 'Failed', label: 'Failed' },
                          ]}
                        />
                      </div>
                    </div>
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                          In mock group
                        </label>
                        <SearchableSelect
                          value={formData.interviewReadiness?.inMockGroup || ''}
                          onChange={(e) =>
                            dispatch({
                              type: 'UPDATE_NESTED_FIELD',
                              section: 'interviewReadiness',
                              field: 'inMockGroup',
                              value: e.target.value,
                            })
                          }
                          className="w-full"
                          placeholder="—"
                          options={[
                            { value: 'Yes', label: 'Yes' },
                            { value: 'No', label: 'No' },
                          ]}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                          In underprepared group
                        </label>
                        <SearchableSelect
                          value={formData.interviewReadiness?.inUnderpreparedGroup || ''}
                          onChange={(e) =>
                            dispatch({
                              type: 'UPDATE_NESTED_FIELD',
                              section: 'interviewReadiness',
                              field: 'inUnderpreparedGroup',
                              value: e.target.value,
                            })
                          }
                          className="w-full"
                          placeholder="—"
                          options={[
                            { value: 'Yes', label: 'Yes' },
                            { value: 'No', label: 'No' },
                          ]}
                        />
                      </div>
                    </div>
                    </div>

              {/* Insurance Information */}
              <div id="auf-insurance" className="auf-form-section scroll-mt-28 mb-4 border-t pt-4">
                <h4 className="font-semibold mb-3 text-sm">🛡️ Insurance Information</h4>
                
                {/* Insurance Taken */}
                <div className="mb-4">
                  <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                    Insurance Taken?
                  </label>
                  <div className="flex gap-4 mb-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="insuranceTaken"
                        value="Yes"
                        checked={formData.insuranceTaken === 'Yes' || formData.insuranceTaken === true || formData.insuranceTaken === 'true'}
                        onChange={(e) => {
                          const value = e.target.value === 'Yes' ? 'Yes' : 'No';
                          dispatch({ type: 'UPDATE_FIELD', field: 'insuranceTaken', value: value });
                        }}
                        className="mr-2"
                      />
                      <span style={{ color: 'var(--color-text)' }}>Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="insuranceTaken"
                        value="No"
                        checked={formData.insuranceTaken === 'No' || formData.insuranceTaken === false || formData.insuranceTaken === 'false' || formData.insuranceTaken === '' || formData.insuranceTaken === null || formData.insuranceTaken === undefined}
                        onChange={(e) => {
                          const value = e.target.value === 'No' ? 'No' : 'Yes';
                          dispatch({ type: 'UPDATE_FIELD', field: 'insuranceTaken', value: value });
                        }}
                        className="mr-2"
                      />
                      <span style={{ color: 'var(--color-text)' }}>No</span>
                    </label>
                  </div>
                </div>

                {/* Insurance Deactivated - Show only if course is completed */}
                {(formData.courseCompletionDate || formData.batchStatus === 'Completed') && (
                  <div className="mb-4">
                    <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                      Insurance Deactivated?
                    </label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="insuranceDeactivated"
                          value="Yes"
                          checked={formData.insuranceDeactivated === 'Yes' || formData.insuranceDeactivated === true || formData.insuranceDeactivated === 'true'}
                          onChange={(e) => {
                            const value = e.target.value === 'Yes' ? 'Yes' : 'No';
                            dispatch({ type: 'UPDATE_FIELD', field: 'insuranceDeactivated', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="insuranceDeactivated"
                          value="No"
                          checked={formData.insuranceDeactivated === 'No' || formData.insuranceDeactivated === false || formData.insuranceDeactivated === 'false' || formData.insuranceDeactivated === '' || formData.insuranceDeactivated === null || formData.insuranceDeactivated === undefined}
                          onChange={(e) => {
                            const value = e.target.value === 'No' ? 'No' : 'Yes';
                            dispatch({ type: 'UPDATE_FIELD', field: 'insuranceDeactivated', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>No</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Certificate Issued - Show only when course is completed */}
                {(formData.courseCompletionDate || formData.batchStatus === 'Completed') && (
                  <div className="mb-4">
                    <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                      Certificate Issued?
                    </label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="certificationIssued"
                          value="Yes"
                          checked={formData.certificationIssued === 'Yes' || formData.certificationIssued === true || formData.certificationIssued === 'true'}
                          onChange={(e) => {
                            const value = e.target.value === 'Yes' ? 'Yes' : 'No';
                            dispatch({ type: 'UPDATE_FIELD', field: 'certificationIssued', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="certificationIssued"
                          value="No"
                          checked={formData.certificationIssued === 'No' || formData.certificationIssued === false || formData.certificationIssued === 'false' || formData.certificationIssued === '' || formData.certificationIssued === null || formData.certificationIssued === undefined}
                          onChange={(e) => {
                            const value = e.target.value === 'No' ? 'No' : 'Yes';
                            dispatch({ type: 'UPDATE_FIELD', field: 'certificationIssued', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>No</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Video/Testimony Received */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                      Video/Testimony Received?
                    </label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="videoTestimonyReceived"
                          value="Yes"
                          checked={formData.videoTestimonyReceived === 'Yes' || formData.videoTestimonyReceived === true || formData.videoTestimonyReceived === 'true'}
                          onChange={(e) => {
                            const value = e.target.value === 'Yes' ? 'Yes' : 'No';
                            dispatch({ type: 'UPDATE_FIELD', field: 'videoTestimonyReceived', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="videoTestimonyReceived"
                          value="No"
                          checked={formData.videoTestimonyReceived === 'No' || formData.videoTestimonyReceived === false || formData.videoTestimonyReceived === 'false' || formData.videoTestimonyReceived === '' || formData.videoTestimonyReceived === null || formData.videoTestimonyReceived === undefined}
                          onChange={(e) => {
                            const value = e.target.value === 'No' ? 'No' : 'Yes';
                            dispatch({ type: 'UPDATE_FIELD', field: 'videoTestimonyReceived', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>No</span>
                      </label>
                    </div>
                  </div>

                {/* Testimony Received */}
                <div className="mb-4">
                    <label className="block mb-2 font-medium text-sm" style={{ color: 'var(--color-text)' }}>
                      Testimony Received?
                    </label>
                    <div className="flex gap-4 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="testimonyReceived"
                          value="Yes"
                          checked={formData.testimonyReceived === 'Yes' || formData.testimonyReceived === true || formData.testimonyReceived === 'true'}
                          onChange={(e) => {
                            const value = e.target.value === 'Yes' ? 'Yes' : 'No';
                            dispatch({ type: 'UPDATE_FIELD', field: 'testimonyReceived', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>Yes</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="testimonyReceived"
                          value="No"
                          checked={formData.testimonyReceived === 'No' || formData.testimonyReceived === false || formData.testimonyReceived === 'false' || formData.testimonyReceived === '' || formData.testimonyReceived === null || formData.testimonyReceived === undefined}
                          onChange={(e) => {
                            const value = e.target.value === 'No' ? 'No' : 'Yes';
                            dispatch({ type: 'UPDATE_FIELD', field: 'testimonyReceived', value: value });
                          }}
                          className="mr-2"
                        />
                        <span style={{ color: 'var(--color-text)' }}>No</span>
                      </label>
                    </div>
                  </div>
              </div>

              {/* Counselling Sessions */}
              <div id="auf-counselling" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4 mb-4">
                <h3 className="font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
                  📋 Counselling Sessions
                </h3>
                {(Array.isArray(formData.counsellingSessions) 
                  ? formData.counsellingSessions 
                  : initialFormData.counsellingSessions).map((session, index) => {
                  return (
                  <div key={index} className="mb-4 p-4 border rounded-lg" style={{ 
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)'
                  }}>
                    <h4 className="font-medium mb-3" style={{ color: 'var(--color-text)' }}>
                      Session {index + 1}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                          Date
                        </label>
                        <input
                          type="date"
                          value={session.sessionDate}
                          onChange={(e) => dispatch({
                            type: 'UPDATE_COUNSELLING_SESSION',
                            index,
                            field: 'sessionDate',
                            value: e.target.value
                          })}
                          className="w-full px-3 py-2 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(session.sessionDate)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                          Counselor Name
                        </label>
                        <SearchableSelect
                          value={session.counselorId}
                          onChange={(e) => dispatch({
                            type: 'UPDATE_COUNSELLING_SESSION',
                            index,
                            field: 'counselorId',
                            value: e.target.value
                          })}
                          className="w-full"
                          placeholder="Select Counselor"
                          options={counselors}
                          getOptionValue={(counselor) => (typeof counselor === 'object' ? counselor.id : counselor)}
                          getOptionLabel={(counselor) =>
                            typeof counselor === 'object'
                              ? (counselor.counselorName || counselor.name || `${counselor.firstName || ''} ${counselor.lastName || ''}`.trim())
                              : counselor
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>
                          Remarks
                        </label>
                  <input
                    type="text"
                          value={session.remarks}
                          onChange={(e) => dispatch({
                            type: 'UPDATE_COUNSELLING_SESSION',
                            index,
                            field: 'remarks',
                            value: e.target.value
                          })}
                          placeholder="Enter remarks"
                          className="w-full px-3 py-2 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(session.remarks)}
                  />
                </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Follow-Up Needed */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                  Follow-Up Needed
                </label>
                <SearchableSelect
                  name="followUpNeeded"
                  value={formData.followUpNeeded ? 'Y' : 'N'}
                  onChange={(e) => dispatch({ 
                    type: 'UPDATE_FIELD', 
                    field: 'followUpNeeded', 
                    value: e.target.value === 'Y' 
                  })}
                  required
                  className="w-full"
                  options={[
                    { value: 'N', label: 'No' },
                    { value: 'Y', label: 'Yes' },
                  ]}
                />
              </div>

              <div id="auf-education" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                <EducationFields
                  formData={formData}
                  dispatch={dispatch}
                  educationOptions={educationOptions}
                  universityOptions={universityOptions}
                />
                {/* Additional Education Details */}
                <div className="border-t pt-4 mt-4">
                  <h3 className="font-semibold mb-2">📄 Additional Education Details</h3>

                  <label className="block mb-2 font-medium">Have you done Diploma?</label>
                  <label className="mr-4">
                    <input
                      type="radio"
                      name="diplomaDone"
                      value="Yes"
                      checked={formData.diplomaDone === 'Yes'}
                      onChange={handleChange}
                      className="mr-1"
                    /> Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="diplomaDone"
                      value="No"
                      checked={formData.diplomaDone === 'No'}
                      onChange={handleChange}
                      className="mr-1"
                    /> No
                  </label>

                  {/* Show specialization and semester only if Diploma is "Yes" */}
                  {formData.diplomaDone === 'Yes' && (
                    <>
                      <input
                        type="text"
                        name="stream"
                        placeholder="Stream / Specialization"
                        value={formData.stream}
                        onChange={handleChange}
                        className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.stream)}
                      />

                      <input
                        type="text"
                        name="semester"
                        placeholder="Semester (if not passout)"
                        value={formData.semester}
                        onChange={handleChange}
                        className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.semester)}
                      />
                    </>
                  )}

                  <MarksFields formData={formData} dispatch={dispatch} />

                  <input
                    type="number"
                    name="tenthYear"
                    placeholder="10th Passing Year"
                    value={formData.tenthYear}
                    onChange={handleChange}
                    className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                    style={getFieldStyle(formData.tenthYear)}
                  />

                  <input
                    type="number"
                    name="twelfthYear"
                    placeholder="12th / Diploma Passing Year"
                    value={formData.twelfthYear}
                    onChange={handleChange}
                    className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                    style={getFieldStyle(formData.twelfthYear)}
                  />

                  <input
                    type="number"
                    name="btechYear"
                    placeholder="B.Tech Passing Year"
                    value={formData.btechYear}
                    onChange={handleChange}
                    className="w-full mb-3 px-4 py-3 theme-input rounded-lg theme-focus"
                    style={getFieldStyle(formData.btechYear)}
                  />
                </div>

                {/* Location Section - Student Role */}
                <div className="border-t pt-4 mt-4">
                  <LocationDropdowns
                    selectedCountry={formData.country}
                    selectedState={formData.state}
                    selectedCity={formData.cityId}
                    onCountryChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'country', value })}
                    onStateChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'state', value })}
                    onCityChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'cityId', value })}
                    getFieldStyle={getFieldStyle}
                  />
                  
                  <SearchableSelect
                    name="heardFrom"
                    value={formData.heardFrom || ''}
                    onChange={handleChange}
                    className="w-full mb-4"
                    placeholder="How did you hear about us?"
                    options={heardFromOptions}
                    getOptionValue={(option) => (typeof option === 'object' ? option.id : option)}
                    getOptionLabel={(option) => (typeof option === 'object' ? option.value : option)}
                  />
                  
                  <textarea
                    name="motivation"
                    placeholder="What motivated you to join?"
                    value={formData.motivation || ''}
                    onChange={handleChange}
                    className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                    style={{ 
                      ...getFieldStyle(formData.motivation),
                      minHeight: '100px'
                    }}
                  />

                  <div id="auf-joining-kit" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Joining Kit Information</h3>
                    <div className="mb-4">
                      <label className="block mb-2 font-medium" style={{ color: 'var(--color-text)' }}>
                        Joining Kit to be sent on:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitAddress"
                            value="current"
                            checked={formData.joiningKitAddress === 'current'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Current Address</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitAddress"
                            value="permanent"
                            checked={formData.joiningKitAddress === 'permanent'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Permanent Address</span>
                        </label>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block mb-2 font-medium" style={{ color: 'var(--color-text)' }}>
                        Joining Kit Received:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitReceived"
                            value="Yes"
                            checked={formData.joiningKitReceived === 'Yes'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="joiningKitReceived"
                            value="No"
                            checked={formData.joiningKitReceived === 'No'}
                            onChange={handleChange}
                            className="mr-2"
                          />
                          <span style={{ color: 'var(--color-text)' }}>No</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div id="auf-parents" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                    <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Parents Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        name="parentsName"
                        placeholder="Parents Name"
                        value={formData.parentsName || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.parentsName)}
                      />
                      <input
                        type="text"
                        name="parentsPhone"
                        placeholder="Parents Phone Number"
                        value={formData.parentsPhone || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.parentsPhone)}
                      />
                    </div>
                    <input
                      type="email"
                      name="parentsEmail"
                      placeholder="Parents Email"
                      value={formData.parentsEmail || ''}
                      onChange={handleChange}
                      className="w-full px-4 py-3 theme-input rounded-lg theme-focus mb-4"
                      style={getFieldStyle(formData.parentsEmail)}
                    />
                  </div>
                </div>

                <div id="auf-placement" className="auf-form-section scroll-mt-28">
                <PlacementFields
                  formData={formData}
                  dispatch={dispatch}
                  companyOptions={companyOptions}
                  onCompanyOptionsUpdate={refreshCompanyOptions}
                  token={token}
                />
                </div>
                </div>
                  </>
                )}

                {/* Non-Student Role Additional Fields */}
                {!isStudentRole && (
                  <>
                    {/* Additional Personal Information */}
                    <div id="auf-staff-personal" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                      <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Additional Personal Information</h3>
                      <AdminUserAdditionalContactFields
                        formData={formData}
                        onChange={handleChange}
                        getFieldStyle={getFieldStyle}
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <input
                          type="text"
                          name="currentCity"
                          placeholder="Current City"
                          value={formData.currentCity || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.currentCity)}
                        />
                        <DateInput
                          name="dob"
                          value={formData.dob}
                          onChange={handleChange}
                          placeholder="Date of Birth"
                          style={getFieldStyle(formData.dob)}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <input
                          type="text"
                          name="address"
                          placeholder="Current Address"
                          value={formData.address || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.address)}
                        />
                        <input
                          type="text"
                          name="pincode"
                          placeholder="Pincode"
                          value={formData.pincode || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.pincode)}
                        />
                      </div>
                      
                      <div className="mb-4">
                        <input
                          type="text"
                          name="permanentAddress"
                          placeholder="Permanent Address"
                          value={formData.permanentAddress || ''}
                          onChange={handleChange}
                          className="w-full px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.permanentAddress)}
                        />
                      </div>
                      
                      {/* Joining Kit Information */}
                      <div className="mb-4">
                        <label className="block mb-2 font-medium" style={{ color: 'var(--color-text)' }}>
                          Joining Kit to be sent on:
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="joiningKitAddress"
                              value="current"
                              checked={formData.joiningKitAddress === 'current'}
                              onChange={handleChange}
                              className="mr-2"
                            />
                            <span style={{ color: 'var(--color-text)' }}>Current Address</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="joiningKitAddress"
                              value="permanent"
                              checked={formData.joiningKitAddress === 'permanent'}
                              onChange={handleChange}
                              className="mr-2"
                            />
                            <span style={{ color: 'var(--color-text)' }}>Permanent Address</span>
                          </label>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block mb-2 font-medium" style={{ color: 'var(--color-text)' }}>
                          Joining Kit Received:
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="joiningKitReceived"
                              value="Yes"
                              checked={formData.joiningKitReceived === 'Yes'}
                              onChange={handleChange}
                              className="mr-2"
                            />
                            <span style={{ color: 'var(--color-text)' }}>Yes</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="joiningKitReceived"
                              value="No"
                              checked={formData.joiningKitReceived === 'No'}
                              onChange={handleChange}
                              className="mr-2"
                            />
                            <span style={{ color: 'var(--color-text)' }}>No</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Guardian Information */}
                    <div id="auf-staff-guardian" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                      <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Guardian Information</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <input
                          type="text"
                          name="guardianName"
                          placeholder="Guardian Name"
                          value={formData.guardianName || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.guardianName)}
                        />
                        <input
                          type="text"
                          name="guardianContact"
                          placeholder="Guardian Contact Number"
                          value={formData.guardianContact || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.guardianContact)}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <input
                          type="email"
                          name="guardianEmail"
                          placeholder="Guardian Email"
                          value={formData.guardianEmail || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.guardianEmail)}
                        />
                      </div>
                    </div>

                    {/* Professional Information */}
                    <div id="auf-staff-professional" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                      <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Professional Information</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <input
                          type="text"
                          name="employeeId"
                          placeholder="Employee ID"
                          value={formData.employeeId || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.employeeId)}
                        />
                        <input
                          type="number"
                          name="experienceYears"
                          placeholder="Years of Experience"
                          value={formData.experienceYears || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.experienceYears)}
                        />
                        <input
                          type="text"
                          name="availability"
                          placeholder="Availability Status"
                          value={formData.availability || ''}
                          onChange={handleChange}
                          className="px-4 py-3 theme-input rounded-lg theme-focus"
                          style={getFieldStyle(formData.availability)}
                        />
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div id="auf-staff-additional" className="auf-form-section scroll-mt-28 border-t pt-4 mt-4">
                      <h3 className="text-lg font-semibold mb-4 theme-text-gradient">Additional Information</h3>
                      
                      <textarea
                        name="bio"
                        placeholder="Biography / About yourself"
                        value={formData.bio || ''}
                        onChange={handleChange}
                        className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          ...getFieldStyle(formData.bio),
                          minHeight: '100px'
                        }}
                      />
                      
                      <textarea
                        name="notes"
                        placeholder="Additional Notes"
                        value={formData.notes || ''}
                        onChange={handleChange}
                        className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                        style={{ 
                          ...getFieldStyle(formData.notes),
                          minHeight: '80px'
                        }}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input
                        type="text"
                        name="department"
                        placeholder="Department"
                        value={formData.department || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.department)}
                      />
                      <input
                        type="text"
                        name="resumeUrl"
                        placeholder="Resume URL"
                        value={formData.resumeUrl || ''}
                        onChange={handleChange}
                        className="px-4 py-3 theme-input rounded-lg theme-focus"
                        style={getFieldStyle(formData.resumeUrl)}
                      />
                    </div>

                    {/* Location Section */}
                    <LocationDropdowns
                      selectedCountry={formData.country}
                      selectedState={formData.state}
                      selectedCity={formData.cityId}
                      onCountryChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'country', value })}
                      onStateChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'state', value })}
                      onCityChange={(value) => dispatch({ type: 'UPDATE_FIELD', field: 'cityId', value })}
                      className="border-t pt-4 mt-4"
                      getFieldStyle={getFieldStyle}
                    />
                    
                  <SearchableSelect
                    name="heardFrom"
                    value={formData.heardFrom || ''}
                    onChange={handleChange}
                    className="w-full mb-4"
                    placeholder="How did you hear about us?"
                    options={heardFromOptions}
                    getOptionValue={(option) => String(typeof option === 'object' ? option.id : option)}
                    getOptionLabel={(option) => (typeof option === 'object' ? option.value : option)}
                  />
                    <textarea
                      name="motivation"
                      placeholder="What motivated you to join?"
                      value={formData.motivation || ''}
                      onChange={handleChange}
                      className="w-full mb-4 px-4 py-3 theme-input rounded-lg theme-focus"
                      style={{ 
                        ...getFieldStyle(formData.motivation),
                        minHeight: '100px'
                      }}
                    />
                  </>
                )}

                {/* Profile Preference - Common for all roles */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                    Profile Preference
                  </label>
                  <SearchableSelect
                    name="profilePreference"
                    value={formData.profilePreference}
                    onChange={handleChange}
                    className="w-full"
                    placeholder="Profile Choice or Preference"
                    options={[
                      { value: 'Physical Design', label: 'Physical Design' },
                      { value: 'Analog Design', label: 'Analog Design' },
                      { value: 'DV Profile', label: 'DV Profile' },
                    ]}
                  />
                </div>
              </>
            )}
          </div>

          {/* Update User Button - At the bottom */}
          <div id="auf-submit" className="auf-form-section scroll-mt-28 mt-8 pt-6 border-t flex justify-center">
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
            >
              {loading ? "Updating..." : "✏️ Update User"}
            </button>
          </div>

          {isStudentRole && editData?.student?.id ? (
            <div id="auf-change-history" className="auf-form-section scroll-mt-28 mt-8 pt-6 border-t">
              <StudentChangeHistory studentId={editData.student.id} limit={30} />
            </div>
          ) : null}
        </form>
      )}

      {/* Message Display */}
      {message && !editData && (
        <MessageDisplay message={message} />
      )}

      <AddEnrollmentModal
        isOpen={showAddEnrollmentModal}
        onClose={() => setShowAddEnrollmentModal(false)}
        studentId={editData?.student?.id}
        enrollments={editData?.student?.enrollments || []}
        batches={batches}
        onSuccess={() => fetchUserToEdit()}
      />
    </StaffPageShell>
  );
};

export default AdminEditUserPage;
